import { isEmpty } from 'lodash'
import { createSelector } from 'reselect'
import VisitStatus from '../../util/visit-status'
import VisitScheduleType from '../../util/visit-schedule-types'
import { isCrisis } from 'util/helpers'

export const getRecords = (state) => state.visit.records
export const getMeta = (state) => state.visit.meta
export const getPendingRecords = (state) => state.visit.pending.records
export const getPendingMeta = (state) => state.visit.pending.meta
export const getRecordArray = createSelector(getRecords, (records = {}) => Object.values(records))
export const getPendingRecordArray = createSelector(getPendingRecords, (records = {}) => Object.values(records))

export const getMemberVisitMeta = (memberId) => createSelector(getMeta, (meta = {}) => meta[memberId])
export const getMemberPendingVisitMeta = (memberId) => createSelector(getPendingMeta, (meta = {}) => meta[memberId])

export const getVisit = (visitId) => createSelector(getRecords, (records = {}) => records[visitId])

export const getVisitByPendingId = (pendingVisitId) =>
  createSelector(getRecords, (records = {}) => {
    return Object.values(records).find((visit) => visit?.pendingVisitId === pendingVisitId)
  })

export const getAllVisits = createSelector(getRecords, (records = {}) => {
  const visits = Object.values(records).filter((rec) => !!rec?.visitId)
  visits.sort((a, b) => {
    if (a.createdAt > b.createdAt) return -1
    return 1
  })
  return visits
})
export const getVisitProviderIds = createSelector(getAllVisits, (records = []) =>
  records?.map(({ providerId }) => providerId),
)

export const getVisitPage = (memberId) => createSelector(getMemberVisitMeta(memberId), (meta = {}) => meta.visitPage)

export const getVisitPageLastFetch = (memberId, page) =>
  createSelector(getMemberVisitMeta(memberId), (meta = {}) => !!meta.pageFetches && meta.pageFetches[page])

export const getPendingVisitPage = (memberId) =>
  createSelector(getMemberPendingVisitMeta(memberId), (meta = {}) => meta.visitPage)

export const getPendingVisitPageLastFetch = (memberId, page) =>
  createSelector(getMemberPendingVisitMeta(memberId), (meta = {}) => !!meta.pageFetches && meta.pageFetches[page])

export const getPendingVisit = (pendingVisitId) =>
  createSelector(getPendingRecords, (records = {}) => records[pendingVisitId])

export const getMemberLastPendingVisit = (memberId) =>
  createSelector(getPendingRecords, (records = {}) =>
    Object.values(records)
      .filter((rec) => rec.memberId === memberId && rec.completedAt)
      .reduce((a, b) => (a.completedAt > b.completedAt ? a : b), {}),
  )

export const getScheduledVisits = createSelector(getPendingRecordArray, (visits = []) => {
  const filtered = visits.filter((visit) => visit.scheduledType === 'scheduled' && visit.status === 'pending')
  filtered.sort((a, b) => (a.scheduledAt > b.scheduledAt ? 1 : -1))
  return filtered
})

export const getScheduledVisitsByType = createSelector(getScheduledVisits, (visits = {}) => {
  const pendingByType = visits.reduce((acc, visit) => {
    if (!acc[visit.visitType]) {
      acc[visit.visitType] = [visit]
    } else {
      acc[visit.visitType].push(visit)
    }
    return acc
  }, {})
  return pendingByType
})

export const getActiveVisit = createSelector(getPendingRecordArray, (visits = []) => {
  const isInProgress = (status) => status === VisitStatus.inProgress
  const isPending = (status) => status === VisitStatus.pending
  const isNowVisit = (scheduledType) => scheduledType === VisitScheduleType.now
  const { inProgress, pending } = visits.reduce(
    (acc, visit) => {
      if (isCrisis(visit.visitType)) return acc

      if (isInProgress(visit.status)) {
        acc.inProgress.push(visit)
      } else if (isPending(visit.status) && isNowVisit(visit.scheduledType)) {
        acc.pending.push(visit)
      }
      return acc
    },
    { inProgress: [], pending: [] },
  )
  if (!isEmpty(inProgress)) {
    inProgress.sort((a, b) => (a.scheduledAt > b.scheduledAt ? -1 : 1))
    const [first] = inProgress
    return first
  }
  if (!isEmpty(pending)) {
    pending.sort((a, b) => (a.scheduledAt > b.scheduledAt ? -1 : 1))
    const [first] = pending
    return first
  }
  return null
})

export const getCreateVisit = createSelector(getRecords, (records = {}) => records.create)
